import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ConnectionsSummary } from '@jordan/types';
import { Alert, Badge, Breadcrumb, Button, PageHeader, SettingsPanel, Spinner, Switch } from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api';

export function ConnectionsPage() {
  const { search } = useLocation();
  const [data, setData] = useState<ConnectionsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<ConnectionsSummary>('/api/connections'));
    } catch {
      setError('Failed to load connections.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(search);
    if (p.get('google') === 'connected') setNotice('Google account connected.');
    else if (p.get('zoom') === 'connected') setNotice('Zoom account connected.');
    else if (p.get('quickbooks') === 'connected') setNotice('QuickBooks company connected.');
    else if (p.get('error')) setError(`Couldn’t connect ${p.get('error')} — please try again.`);
  }, [search]);

  const PROVIDER_LABELS: Record<string, string> = { google: 'Google', zoom: 'Zoom', quickbooks: 'QuickBooks' };
  const connect = async (provider: 'google' | 'zoom' | 'quickbooks') => {
    setError(null);
    try {
      const { url } = await apiGet<{ url: string }>(`/api/connections/${provider}/connect`);
      window.location.href = url;
    } catch {
      setError(`${PROVIDER_LABELS[provider]} isn’t configured yet (missing OAuth credentials).`);
    }
  };

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch {
      setError('Action failed. Please try again.');
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Connections</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Connections' }]} />
        }
      />

      <div className="content">
        {notice && <Alert variant="success" icon="ph-check-circle" dismissible>{notice}</Alert>}
        {error && <Alert variant="danger" icon="ph-warning" dismissible>{error}</Alert>}

        {!data && <Spinner />}

        {data && (
          <>
            {/* Google */}
            <SettingsPanel
              title="Google Calendar"
              description="Connect every Google account you use; a single booking link checks free/busy across all of them."
              action={
                <Button variant="primary" icon="ph-plus" disabled={!data.google.configured} onClick={() => connect('google')}>
                  Connect Google
                </Button>
              }
            >
              <div className="py-3 border-top">
                {!data.google.configured && (
                  <p className="text-muted mb-0">
                    Google isn’t configured yet — add the OAuth client ID/secret to enable connecting.
                  </p>
                )}
                {data.google.configured && data.google.accounts.length === 0 && (
                  <p className="text-muted mb-0">No Google accounts connected yet.</p>
                )}
                {data.google.accounts.map((acct) => (
                  <div key={acct.id} className="mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-center mb-2">
                      <i className="ph-google-logo me-2 fs-5" />
                      <span className="fw-semibold">{acct.accountEmail}</span>
                      {acct.syncError && (
                        <Badge variant="danger" soft className="ms-2">
                          Needs reconnect
                        </Badge>
                      )}
                      <div className="ms-auto d-flex gap-2">
                        <Button variant="light" size="sm" icon="ph-arrows-clockwise" onClick={() => act(() => apiPost(`/api/connections/calendar-accounts/${acct.id}/refresh`))}>
                          Refresh
                        </Button>
                        <Button variant="light" size="sm" className="text-danger" icon="ph-trash" onClick={() => act(() => apiDelete(`/api/connections/calendar-accounts/${acct.id}`))}>
                          Disconnect
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Calendar</th>
                            <th className="text-center">Check availability</th>
                            <th className="text-center">Write bookings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {acct.calendars.map((cal) => (
                            <tr key={cal.id}>
                              <td>
                                {cal.backgroundColor && (
                                  <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: cal.backgroundColor }} />
                                )}
                                {cal.summary}
                                {cal.isPrimary && <Badge variant="secondary" soft className="ms-2">Primary</Badge>}
                              </td>
                              <td className="text-center">
                                <Switch label="" checked={cal.syncEnabled} onChange={(e) => act(() => apiPatch(`/api/connections/calendars/${cal.id}`, { syncEnabled: e.target.checked }))} />
                              </td>
                              <td className="text-center">
                                <Switch
                                  label=""
                                  checked={cal.writable && cal.canWrite}
                                  disabled={!cal.canWrite}
                                  title={cal.canWrite ? undefined : 'Read-only in Google — can’t write bookings here'}
                                  onChange={(e) => act(() => apiPatch(`/api/connections/calendars/${cal.id}`, { writable: e.target.checked }))}
                                />
                                {!cal.canWrite && <div className="text-muted fs-sm">Read-only</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </SettingsPanel>

            {/* Zoom */}
            <SettingsPanel
              title="Zoom"
              description="Connect Zoom accounts to host event-type meetings on Zoom instead of Google Meet."
              action={
                <Button variant="primary" icon="ph-plus" disabled={!data.zoom.configured} onClick={() => connect('zoom')}>
                  Connect Zoom
                </Button>
              }
            >
              <div className="py-3 border-top">
                {!data.zoom.configured && (
                  <p className="text-muted mb-0">Zoom isn’t configured yet — add the OAuth client ID/secret to enable connecting.</p>
                )}
                {data.zoom.configured && data.zoom.accounts.length === 0 && (
                  <p className="text-muted mb-0">No Zoom accounts connected yet.</p>
                )}
                {data.zoom.accounts.map((z) => (
                  <div key={z.id} className="d-flex align-items-center py-2 border-bottom">
                    <i className="ph-video-camera me-2 fs-5" />
                    <span className="fw-semibold">{z.displayName ?? z.accountEmail}</span>
                    <span className="text-muted ms-2">{z.accountEmail}</span>
                    <Button variant="light" size="sm" className="text-danger ms-auto" icon="ph-trash" onClick={() => act(() => apiDelete(`/api/connections/zoom-accounts/${z.id}`))}>
                      Disconnect
                    </Button>
                  </div>
                ))}
              </div>
            </SettingsPanel>

            {/* QuickBooks */}
            <SettingsPanel
              title="QuickBooks"
              description="Connect your QuickBooks Online company to sync billing and expenses."
              action={
                <Button variant="primary" icon="ph-plus" disabled={!data.quickbooks.configured} onClick={() => connect('quickbooks')}>
                  Connect QuickBooks
                </Button>
              }
            >
              <div className="py-3 border-top">
                {!data.quickbooks.configured && (
                  <p className="text-muted mb-0">QuickBooks isn’t configured yet — add the OAuth client ID/secret to enable connecting.</p>
                )}
                {data.quickbooks.configured && data.quickbooks.accounts.length === 0 && (
                  <p className="text-muted mb-0">No QuickBooks company connected yet.</p>
                )}
                {data.quickbooks.accounts.map((q) => (
                  <div key={q.id} className="d-flex align-items-center py-2 border-bottom">
                    <i className="ph-buildings me-2 fs-5" />
                    <span className="fw-semibold">{q.companyName ?? 'QuickBooks company'}</span>
                    <span className="text-muted ms-2">Realm {q.realmId}</span>
                    <Button variant="light" size="sm" className="text-danger ms-auto" icon="ph-trash" onClick={() => act(() => apiDelete(`/api/connections/quickbooks-accounts/${q.id}`))}>
                      Disconnect
                    </Button>
                  </div>
                ))}
              </div>
            </SettingsPanel>
          </>
        )}
      </div>
    </AppChrome>
  );
}
